import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import styled from "styled-components";
import Api from "api/Api";
import Icon from "layout/atoms/Icon";
import {ReactComponent as EditSvg} from "assets/icons/edit1.svg";
import {ReactComponent as TrashSvg} from "assets/icons/trash.svg";
import {ReactComponent as DotsVerticalSvg} from "assets/icons/dots-vertical.svg";
import {ReactComponent as CheckmarkSvg} from "assets/icons/checkmark/checkmark.svg";
import {selectCompanyInfo} from "store/companySlice";
import MainContainer from "layout/atoms/MainContainer";

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 0;

  @media (max-width: 768px) {
    padding: 0 0 80px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Header = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: var(--black);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Description = styled.p`
  font-size: 14px;
  color: var(--gray);
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;

    button {
      width: 100%;
    }
  }
`;

const NewContactButton = styled.button`
  background: var(--brand-ideolo);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #4bc235;
    box-shadow: 0 2px 8px rgba(83, 210, 61, 0.3);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const ImportButton = styled.button`
  background: white;
  color: var(--brand-ideolo);
  border: 1px solid var(--brand-ideolo);
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f6f8fa;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const SearchBar = styled.input`
  width: 100%;
  max-width: 400px;
  padding: 10px 16px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 24px;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--brand-ideolo);
  }

  &::placeholder {
    color: var(--gray);
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ContactsTable = styled.div`
  background: var(--content-box-bg);
  border: 1px solid #d0d7de;
  border-radius: 8px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 3fr 100px;
  background: #f6f8fa;
  padding: 12px 20px;
  font-size: 12px;
  font-weight: 600;
  color: var(--gray);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #d0d7de;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr 60px;

    .col-tags {
      display: none;
    }
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 3fr 100px;
  padding: 16px 20px;
  border-bottom: 1px solid #e1e4e8;
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f6f8fa;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr 60px;

    .col-tags {
      display: none;
    }
  }
`;

const CellName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--black);
`;

const CellPhone = styled.div`
  font-size: 14px;
  color: var(--black);
  font-family: monospace;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const Tag = styled.span`
  background: #ddf4ff;
  color: #0969da;
  padding: 1px 6px;
  border-radius: 2px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.5;
`;

const ActionsCell = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  color: var(--gray);
  font-size: 18px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f6f8fa;
    color: var(--black);
  }
`;

const DropdownMenu = styled.div`
  position: fixed;
  background: white;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 120px;
  z-index: 1001;
  overflow: hidden;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: white;
  text-align: left;
  font-size: 13px;
  cursor: pointer;
  color: var(--black);
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #f6f8fa;
  }

  &.delete {
    color: #d1242f;

    &:hover {
      background: #ffebe9;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--gray);
  font-size: 14px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 20px 16px;
    width: 95%;
    max-height: 90vh;
    border-radius: 8px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: var(--black);
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  font-size: 24px;
  color: var(--gray);
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: #f6f8fa;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--black);
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--brand-ideolo);
  }
`;

const PhoneInputGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const CountrySelect = styled.select`
  padding: 10px 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  min-width: 120px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--brand-ideolo);
  }

  @media (max-width: 480px) {
    min-width: 90px;
    font-size: 13px;
    padding: 10px 6px;
  }
`;

const PhoneInput = styled(Input)`
  flex: 1;
`;

const TagInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  min-height: 42px;
  align-items: center;

  &:focus-within {
    border-color: var(--brand-ideolo);
  }
`;

const TagInputField = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  min-width: 120px;
`;

const RemovableTag = styled(Tag)`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;

  &:hover {
    background: #b6e3ff;
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;

  @media (max-width: 480px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const CancelButton = styled(Button)`
  background: white;
  color: var(--gray);
  border: 1px solid #d0d7de;

  &:hover {
    background: #f6f8fa;
  }
`;

const SaveButton = styled(Button)`
  background: var(--brand-ideolo);
  color: white;
  border: none;

  &:hover {
    background: #4bc235;
    box-shadow: 0 2px 8px rgba(83, 210, 61, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 32px;
  right: 32px;
  background: #108548;
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-weight: 500;
  z-index: 2000;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const ToastIcon = styled.span`
  font-size: 18px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 300px;
  padding: 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 13px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--brand-ideolo);
  }

  &::placeholder {
    color: var(--gray);
  }
`;

const HelpText = styled.div`
  font-size: 13px;
  color: var(--gray);
  margin-bottom: 16px;
  line-height: 1.5;
`;

const CodeExample = styled.code`
  background: #f6f8fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
  color: #24292f;
`;

const ValidationResults = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: #f6f8fa;
  border-radius: 6px;
  font-size: 13px;
`;

const ValidationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-weight: 600;
`;

const ValidationSummary = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
`;

const ValidationStat = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ type }) =>
    type === 'success' ? '#108548' :
    type === 'error' ? '#d1242f' :
    'var(--gray)'
  };
`;

const ErrorsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  margin-top: 12px;
`;

const ErrorItem = styled.div`
  padding: 8px;
  background: white;
  border-left: 3px solid #d1242f;
  margin-bottom: 8px;
  border-radius: 4px;
  font-size: 12px;
`;

const ErrorLine = styled.div`
  font-weight: 600;
  color: #d1242f;
  margin-bottom: 4px;
`;

const ErrorMessage = styled.div`
  color: var(--gray);
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid var(--brand-ideolo);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function Contacts() {
  const { t } = useTranslation("translation", {
    keyPrefix: "messaging.contacts",
  });
  const companyInfo = useSelector(selectCompanyInfo);
  const [companyId, setCompanyId] = useState(companyInfo.id);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    tags: [],
  });
  const [tagInput, setTagInput] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [countryCode, setCountryCode] = useState("+48"); // Default to Poland
  const nameInputRef = useRef(null);
  const api = new Api();

  // Import CSV state
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvData, setCsvData] = useState("");
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Dropdown menu state
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const fetchData = async () => {
      // Company info is loaded asynchronously; wait until it's available
      const currentCompanyId = companyInfo.id;
      if (!currentCompanyId) {
        return;
      }

      try {
        setCompanyId(currentCompanyId);

        const contactsData = await api.getContacts(currentCompanyId);
        setContacts(contactsData);
        setFilteredContacts(contactsData);
      } catch (error) {
        console.error("Failed to fetch contacts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo.id]);

  useEffect(() => {
    const filtered = contacts.filter((contact) => {
      const query = searchQuery.toLowerCase();
      return (
        contact.name.toLowerCase().includes(query) ||
        contact.phone.toLowerCase().includes(query) ||
        contact.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    });
    setFilteredContacts(filtered);
  }, [searchQuery, contacts]);

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
    };

    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuId]);

  const handleOpenModal = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      // Extract country code from phone if it exists
      // Try to match common country code patterns (1-4 digits after +)
      const phoneMatch = contact.phone.match(/^(\+\d{1,4})/);

      let extractedCountryCode = "+48"; // default
      let phoneWithoutCode = contact.phone;

      if (phoneMatch) {
        const matchedCode = phoneMatch[1];
        // Check if this matches one of our known country codes
        const knownCodes = ["+48", "+1", "+44", "+49", "+33", "+39", "+34", "+31", "+46", "+47", "+45", "+420", "+421"];

        // Try to find the best match (longest matching code)
        for (let i = matchedCode.length; i >= 1; i--) {
          const testCode = matchedCode.substring(0, i + 1); // +1 for the + sign
          if (knownCodes.includes(testCode)) {
            extractedCountryCode = testCode;
            phoneWithoutCode = contact.phone.substring(testCode.length);
            break;
          }
        }
      }

      setCountryCode(extractedCountryCode);
      setFormData({
        name: contact.name,
        phone: phoneWithoutCode,
        tags: [...contact.tags],
      });
    } else {
      setEditingContact(null);
      setFormData({
        name: "",
        phone: "",
        tags: [],
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingContact(null);
    setFormData({
      name: "",
      phone: "",
      tags: [],
    });
    setTagInput("");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddTag = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const tag = tagInput.trim().toLowerCase();
      if (tag && !formData.tags.includes(tag)) {
        setFormData((prev) => ({
          ...prev,
          tags: [...prev.tags, tag],
        }));
        setTagInput("");
      }
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const showSuccessToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  const handleSave = async () => {
    try {
      // Combine country code with phone number
      const fullPhone = countryCode + formData.phone;
      const contactData = {
        ...formData,
        phone: fullPhone,
      };

      if (editingContact) {
        await api.updateContact(companyId, editingContact.id, contactData);
        setContacts((prev) =>
          prev.map((c) =>
            c.id === editingContact.id ? { ...c, ...contactData } : c
          )
        );
        showSuccessToast(t("toast.contactUpdated", { name: formData.name }));
        handleCloseModal();
      } else {
        const newContact = await api.createContact(companyId, contactData);
        setContacts((prev) => [newContact, ...prev]);

        showSuccessToast(t("toast.contactAdded", { name: formData.name }));

        // Clear form and keep modal open for adding another contact
        // Keep country code but clear other fields
        setFormData({
          name: "",
          phone: "",
          tags: [],
        });
        setTagInput("");

        // Focus on name input for next contact
        setTimeout(() => {
          if (nameInputRef.current) {
            nameInputRef.current.focus();
          }
        }, 0);
      }
    } catch (error) {
      console.error("Failed to save contact:", error);
      alert(t("errors.failedToSave"));
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm(t("deleteConfirm"))) {
      return;
    }

    try {
      await api.deleteContact(companyId, contactId);
      setContacts((prev) => prev.filter((c) => c.id !== contactId));
    } catch (error) {
      console.error("Failed to delete contact:", error);
      alert(t("errors.failedToDelete"));
    }
  };

  const handleOpenImportModal = () => {
    setShowImportModal(true);
    setCsvData("");
    setValidationResult(null);
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setCsvData("");
    setValidationResult(null);
  };

  const handleValidateImport = async () => {
    if (!csvData.trim()) {
      alert(t("errors.pasteDataFirst"));
      return;
    }

    setIsValidating(true);
    try {
      const result = await api.validateContactsImport(companyId, csvData);
      setValidationResult(result);
    } catch (error) {
      console.error("Failed to validate import:", error);
      alert(t("errors.failedToValidate"));
    } finally {
      setIsValidating(false);
    }
  };

  const handleExecuteImport = async () => {
    if (!csvData.trim()) {
      alert(t("errors.pasteDataFirst"));
      return;
    }

    if (!validationResult || validationResult.errorCount > 0) {
      if (!window.confirm(
        t("importModal.errorPrompt", {
          count: validationResult?.errorCount || 0,
          validCount: validationResult?.validCount || 0
        })
      )) {
        return;
      }
    }

    setIsImporting(true);
    try {
      const result = await api.executeContactsImport(companyId, csvData);

      // Refresh contacts list
      const contactsData = await api.getContacts(companyId);
      setContacts(contactsData);
      setFilteredContacts(contactsData);

      showSuccessToast(
        t("toast.importSuccess", { count: result.successCount }) +
        (result.errorCount > 0 ? " " + t("toast.importWithErrors", { count: result.errorCount }) : "")
      );

      handleCloseImportModal();
    } catch (error) {
      console.error("Failed to import contacts:", error);
      alert(t("errors.failedToImport"));
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <MainContainer>
    <Container>
      <HeaderSection>
        <HeaderContent>
          <Header>{t("header")}</Header>
          <Description>{t("description")}</Description>
        </HeaderContent>
        <ButtonGroup>
          <ImportButton onClick={handleOpenImportModal}>
            {t("importCsvButton")}
          </ImportButton>
          <NewContactButton onClick={() => handleOpenModal()}>
            {t("newContactButton")}
          </NewContactButton>
        </ButtonGroup>
      </HeaderSection>

      <SearchBar
        type="text"
        placeholder={t("searchPlaceholder")}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {loading ? (
        <EmptyState>{t("loadingContacts")}</EmptyState>
      ) : filteredContacts.length === 0 ? (
        <EmptyState>
          {searchQuery ? t("noContactsFound") : t("noContacts")}
        </EmptyState>
      ) : (
        <ContactsTable>
          <TableHeader>
            <div>{t("table.name")}</div>
            <div>{t("table.phone")}</div>
            <div className="col-tags">{t("table.tags")}</div>
            <div>{t("table.actions")}</div>
          </TableHeader>
          {filteredContacts.map((contact) => (
            <TableRow key={contact.id}>
              <CellName>{contact.name}</CellName>
              <CellPhone>{contact.phone}</CellPhone>
              <TagsContainer className="col-tags">
                {contact.tags.map((tag, index) => (
                  <Tag key={index}>{tag}</Tag>
                ))}
              </TagsContainer>
              <ActionsCell>
                <MenuButton
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    setMenuPosition({
                      top: rect.bottom + 4,
                      left: rect.right - 120, // 120px is min-width of dropdown
                    });
                    setOpenMenuId(openMenuId === contact.id ? null : contact.id);
                  }}
                >
                  <Icon src={DotsVerticalSvg} />
                </MenuButton>
                {openMenuId === contact.id && (
                  <DropdownMenu
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      top: `${menuPosition.top}px`,
                      left: `${menuPosition.left}px`,
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        setOpenMenuId(null);
                        handleOpenModal(contact);
                      }}
                    >
                      <Icon src={EditSvg} /> {t("menu.edit")}
                    </MenuItem>
                    <MenuItem
                      className="delete"
                      onClick={() => {
                        setOpenMenuId(null);
                        handleDelete(contact.id);
                      }}
                    >
                      <Icon src={TrashSvg} /> {t("menu.delete")}
                    </MenuItem>
                  </DropdownMenu>
                )}
              </ActionsCell>
            </TableRow>
          ))}
        </ContactsTable>
      )}

      {/* Contact Modal */}
      <ModalOverlay isOpen={showModal} onClick={handleCloseModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              {editingContact ? t("contactModal.editTitle") : t("contactModal.newTitle")}
            </ModalTitle>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>{t("contactModal.nameLabel")}</Label>
            <Input
              ref={nameInputRef}
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t("contactModal.namePlaceholder")}
            />
          </FormGroup>

          <FormGroup>
            <Label>{t("contactModal.phoneLabel")}</Label>
            <PhoneInputGroup>
              <CountrySelect
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              >
                <option value="+48">🇵🇱 +48 PL</option>
                <option value="+1">🇺🇸 +1 US</option>
                <option value="+44">🇬🇧 +44 UK</option>
                <option value="+49">🇩🇪 +49 DE</option>
                <option value="+33">🇫🇷 +33 FR</option>
                <option value="+39">🇮🇹 +39 IT</option>
                <option value="+34">🇪🇸 +34 ES</option>
                <option value="+31">🇳🇱 +31 NL</option>
                <option value="+46">🇸🇪 +46 SE</option>
                <option value="+47">🇳🇴 +47 NO</option>
                <option value="+45">🇩🇰 +45 DK</option>
                <option value="+420">🇨🇿 +420 CZ</option>
                <option value="+421">🇸🇰 +421 SK</option>
              </CountrySelect>
              <PhoneInput
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder={t("contactModal.phonePlaceholder")}
              />
            </PhoneInputGroup>
          </FormGroup>

          <FormGroup>
            <Label>{t("contactModal.tagsLabel")}</Label>
            <TagInput>
              {formData.tags.map((tag, index) => (
                <RemovableTag key={index} onClick={() => handleRemoveTag(tag)}>
                  {tag} ×
                </RemovableTag>
              ))}
              <TagInputField
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder={t("contactModal.tagsPlaceholder")}
              />
            </TagInput>
          </FormGroup>

          <ModalButtonGroup>
            <CancelButton onClick={handleCloseModal}>{t("contactModal.cancel")}</CancelButton>
            <SaveButton
              onClick={handleSave}
              disabled={!formData.name || !formData.phone}
            >
              {t("contactModal.save")}
            </SaveButton>
          </ModalButtonGroup>
        </ModalContent>
      </ModalOverlay>

      {/* Import CSV Modal */}
      <ModalOverlay isOpen={showImportModal} onClick={handleCloseImportModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>{t("importModal.title")}</ModalTitle>
            <CloseButton onClick={handleCloseImportModal}>×</CloseButton>
          </ModalHeader>

          <HelpText>
            {t("importModal.helpText")}{" "}
            <CodeExample>{t("importModal.format")}</CodeExample>
            <br />
            {t("importModal.example")} <CodeExample>{t("importModal.exampleData")}</CodeExample>
          </HelpText>

          <TextArea
            placeholder={t("importModal.placeholder")}
            value={csvData}
            onChange={(e) => setCsvData(e.target.value)}
          />

          {validationResult && (
            <ValidationResults>
              <ValidationHeader>
                <div>{t("importModal.validationHeader")}</div>
              </ValidationHeader>
              <ValidationSummary>
                <ValidationStat type="success">
                  <span>✓</span>
                  <span>{validationResult.validCount} {t("importModal.valid")}</span>
                </ValidationStat>
                {validationResult.errorCount > 0 && (
                  <ValidationStat type="error">
                    <span>✗</span>
                    <span>{validationResult.errorCount} {t("importModal.errors")}</span>
                  </ValidationStat>
                )}
              </ValidationSummary>

              {validationResult.errors && validationResult.errors.length > 0 && (
                <ErrorsList>
                  {validationResult.errors.map((error, index) => (
                    <ErrorItem key={index}>
                      <ErrorLine>{t("importModal.line")} {error.lineNumber}</ErrorLine>
                      <ErrorMessage>{error.error}</ErrorMessage>
                      {error.rawData && (
                        <ErrorMessage style={{ fontFamily: 'monospace', fontSize: '11px', marginTop: '4px' }}>
                          {error.rawData}
                        </ErrorMessage>
                      )}
                    </ErrorItem>
                  ))}
                </ErrorsList>
              )}
            </ValidationResults>
          )}

          <ModalButtonGroup>
            <CancelButton onClick={handleCloseImportModal}>{t("importModal.cancel")}</CancelButton>
            <SaveButton onClick={handleValidateImport} disabled={isValidating || !csvData.trim()}>
              {isValidating ? (
                <>
                  <LoadingSpinner /> {t("importModal.validating")}
                </>
              ) : (
                t("importModal.validate")
              )}
            </SaveButton>
            {validationResult && validationResult.validCount > 0 && (
              <SaveButton onClick={handleExecuteImport} disabled={isImporting}>
                {isImporting ? (
                  <>
                    <LoadingSpinner /> {t("importModal.importing")}
                  </>
                ) : (
                  t("importModal.import", { count: validationResult.validCount })
                )}
              </SaveButton>
            )}
          </ModalButtonGroup>
        </ModalContent>
      </ModalOverlay>

      {/* Success Toast */}
      {showToast && (
        <Toast>
          <ToastIcon><Icon src={CheckmarkSvg} /></ToastIcon>
          {toastMessage}
        </Toast>
      )}
    </Container>
    </MainContainer>
  );
}

export default Contacts;
